import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, X, Maximize2, ExternalLink } from "lucide-react";

interface PDFPreviewProps {
  url: string;
  title: string;
  isOpen: boolean;
  onClose: () => void;
}

export function PDFPreview({ url, title, isOpen, onClose }: PDFPreviewProps) {
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleLoad = () => {
    setLoading(false);
  };

  const openInNewTab = () => {
    window.open(url, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className={`bg-gray-900 border-gray-700 text-white p-0 ${
          isFullscreen ? 'max-w-[95vw] h-[95vh]' : 'max-w-4xl h-[80vh]'
        }`}
      >
        <DialogHeader className="p-4 border-b border-gray-700 flex flex-row items-center justify-between">
          <DialogTitle className="text-lg font-semibold truncate pr-4">{title}</DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={openInNewTab}
              className="text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        
        <div className="relative flex-1 h-full min-h-0">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            </div>
          )}
          <iframe
            src={`${url}#toolbar=0&navpanes=0`}
            className="w-full h-full border-0"
            onLoad={handleLoad}
            title={title}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
